import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  Input,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/styles'
import { Autocomplete } from '@mui/material'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import {
  AddEntertainmentVideos,
} from 'src/redux/actions'
import { AddVideoImageUrl, getFilePreview } from 'src/util/helpers'
import { uploadToS3 } from 'src/util/uploadToS3'
import * as Yup from 'yup'

import { StatusList } from '../../util/constants'
import DropdownMenu from '../helperComponents/DropdownMenu'
import SubtitleEditor from '../shared/SubtitleEditor'

const validationSchema = Yup.object().shape({
  name: Yup.string().min(1, 'Minimum 1 letters').required('Required'),
  video: Yup.string().required('Please enter video url'),
  category: Yup.string().required('Please select a category'),
  type: Yup.string().oneOf(['normal', '4L'], 'Select a video type').required('Video type is required'),
})

const FORM_FIELDS = {
  SUBTITLE: 'subtitle',
}

const useStyles = makeStyles(() =>
  createStyles({
    invisible: { visibility: 'hidden' },
  })
)

const AddEditVideo = ({ selectedRow, toggleDrawer, refetch, categories }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const slug = searchParams.get('slug');

  const initialValues = {
    name: selectedRow?.name || '',
    video: selectedRow?.video || '',
    type: selectedRow?.type || '4L',
    coin: selectedRow?.coin || '',
    videoIframe: selectedRow?.videoIframe || '',
    imageUrl: selectedRow?.imageUrl || '',
    isActive:
      selectedRow && selectedRow.isActive === undefined
        ? true
        : selectedRow && selectedRow?.isActive,
    category: slug ? selectedRow?.category : selectedRow?.category?._id,
  }
  const [openSubtitleEditor, setOpenSubtitleEditor] = useState(false)
  const classes = useStyles()

  useEffect(() => {
    if (selectedRow?.id) {
      localStorage.setItem('transcriptDataBackup', []);
    }
  }, [])

  const handleCancel = () => {
    toggleDrawer(false)
  }

  const handleSubmit = async values => {
    let dataToSubmit = selectedRow?._id
      ? { ...values, _id: selectedRow._id }
      : { ...values }
    const response = await dispatch(AddEntertainmentVideos(dataToSubmit));
    if (slug && slug !== response?.slug) {
      navigate(`/4l-videos?slug=${response?.slug}`)
    }
    toggleDrawer(false)
    refetch()
  }

  const uploadFile = files => {
    uploadToS3(files[0])
  }

  const handleEditTranscript = () => {
    setOpenSubtitleEditor(!openSubtitleEditor)
  }
  return (
    <>
      <Box sx={{ m: 1 }}>
        <Container>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnBlur={true}
            validateOnChange={false}
          >
            {({
              errors,
              touched,
              values,
              handleBlur,
              handleChange,
              setFieldValue,
              setFieldTouched,
            }) => (
              <Form>
                <Grid container spacing={2}>
                  {/* Video Name */}
                  <Grid item md={6} xs={12}>
                    <TextField
                      error={Boolean(touched.name && errors.name)}
                      fullWidth
                      helperText={touched.name && errors.name}
                      label="Video Name"
                      placeholder="Video Name"
                      name="name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.name}
                      variant="outlined"
                      required
                      size='small'
                    />
                  </Grid>

                  {/* Video URL */}
                  <Grid item md={6} xs={12}>
                    <TextField
                      error={Boolean(touched.video && errors.video)}
                      fullWidth
                      helperText={touched.video && errors.video}
                      label="Video URL"
                      placeholder="Video Link"
                      name="video"
                      type="url"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.video}
                      variant="outlined"
                      required
                      size='small'
                    />
                    < AddVideoImageUrl text="Add full YouTube or video URL." />
                  </Grid>

                  {/* Category */}
                  <Grid item md={6} xs={12}>
                    <Autocomplete
                      value={categories?.find(
                        category => category.key === values?.category
                      ) || null}
                      onChange={(event, newValue) => {
                        setFieldValue('category', newValue?.key || '');
                      }}
                      onBlur={() => setFieldTouched('category', true)}
                      options={categories}
                      name="category"
                      getOptionLabel={option => option?.value || ''}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Category"
                          required
                          error={Boolean(touched.category && errors.category)}
                          helperText={touched.category && errors.category}
                          size='small'
                        />
                      )}
                    />
                  </Grid>

                  {/* Status */}
                  <Grid item md={6} xs={12}>
                    <FormControl fullWidth>
                      <DropdownMenu
                        value={values.isActive}
                        label="Status"
                        name="isActive"
                        isRequired={true}
                        onChange={handleChange}
                        data={StatusList}
                      />
                    </FormControl>
                  </Grid>

                  {/* 4L Video */}
                  <Grid item md={6} xs={12}>
                    <FormControl
                      error={Boolean(errors.type)}
                      component="fieldset"
                      fullWidth
                    >
                      <FormControlLabel
                        label={'4L Video'}
                        control={
                          <Radio
                            name="type"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            checked={values.type === '4L'}
                            value={'4L'}
                          />
                        }
                        htmlFor="type"
                      ></FormControlLabel>
                      {touched.type && errors.type && (
                        <Typography color="error">{errors.type}</Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Transcript */}
                  <Grid item md={6} xs={12}>
                    <FormControl
                      fullWidth
                      className={classes.invisible}
                      style={{ height: '1px' }}
                    >
                      <TextField
                        name={FORM_FIELDS.SUBTITLE}
                        value={values[FORM_FIELDS.SUBTITLE]}
                        size='small'
                      />
                    </FormControl>
                    <Button
                      color="primary"
                      onClick={handleEditTranscript}
                      fullWidth
                      size="medium"
                      type="button"
                      variant="contained"
                    >
                      {selectedRow?.subtitle?.length ||
                        values[FORM_FIELDS.SUBTITLE]
                        ? 'Edit'
                        : 'Upload'}{' '}
                      Transcript
                    </Button>
                    <SubtitleEditor
                      subtitle={
                        selectedRow?.subtitle || values[FORM_FIELDS.SUBTITLE]
                      }
                      handleClose={() => setOpenSubtitleEditor(false)}
                      open={openSubtitleEditor}
                      onSave={value => {
                        setFieldValue(FORM_FIELDS.SUBTITLE, value);
                        setOpenSubtitleEditor(false);
                      }}
                    />
                  </Grid>

                  {/* Video Iframe */}
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      label="Video Iframe"
                      multiline
                      rows={2}
                      placeholder="Video Iframe"
                      name="videoIframe"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.videoIframe}
                      variant="outlined"
                    />
                  </Grid>

                  {/* Difficulty Order */}
                  <Grid item md={6} xs={12}>
                    <TextField
                      error={Boolean(touched.difficultyOrder && errors.difficultyOrder)}
                      fullWidth
                      type="number"
                      helperText={touched.difficultyOrder && errors.difficultyOrder}
                      label="Difficulty Order"
                      placeholder="Difficulty Order"
                      name="difficultyOrder"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.difficultyOrder}
                      variant="outlined"
                      size='small'
                    />
                  </Grid>

                  {/* Sort Order */}
                  <Grid item md={6} xs={12}>
                    <TextField
                      error={Boolean(touched.sortOrder && errors.sortOrder)}
                      fullWidth
                      type="number"
                      helperText={touched.sortOrder && errors.sortOrder}
                      label="Sort Order"
                      placeholder="Sort Order"
                      name="sortOrder"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.sortOrder}
                      variant="outlined"
                      size='small'
                    />
                  </Grid>

                  {/* Video Tags */}
                  <Grid item md={6} xs={12}>
                    <TextField
                      error={Boolean(touched.tags && errors.tags)}
                      fullWidth
                      helperText={touched.tags && errors.tags}
                      label="Video Tags"
                      placeholder="cricket, dance, comedy"
                      name="tags"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.tags}
                      variant="outlined"
                      size='small'
                    />
                  </Grid>

                  {/* Video Coin */}
                  <Grid item md={6} xs={12}>
                    <TextField
                      error={Boolean(touched.coin && errors.coin)}
                      fullWidth
                      helperText={touched.coin && errors.coin}
                      label="Video Coin"
                      placeholder="Video Coin"
                      name="coin"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.coin}
                      variant="outlined"
                      size='small'
                    />
                  </Grid>

                  {/* Video Image Upload */}
                  <Grid item md={12} xs={12}>
                    <Stack direction="row" spacing={2} margin={1}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item md={4} xs={12}>
                          <FormControlLabel
                            label={''}
                            htmlFor={`imageUrl`}
                            control={
                              <>
                                <Input
                                  type="file"
                                  style={{ display: 'none' }}
                                  onChange={e => {
                                    uploadFile(e.target.files);
                                  }}
                                  name={`imageUrl`}
                                  inputProps={{
                                    accept: 'image/*',
                                  }}
                                  id={`imageUrl`}
                                />
                                <Button variant="contained" component="span">
                                  {'Video Image'}
                                </Button>
                              </>
                            }
                          />
                        </Grid>
                        <Grid item md={8} xs={12}>
                          {getFilePreview(values[`imageUrl`])}
                        </Grid>
                      </Grid>
                    </Stack>
                  </Grid>

                  {/* Save & Cancel Buttons */}
                  <Grid item md={6} xs={12}>
                    <Button
                      color="primary"
                      onClick={handleCancel}
                      fullWidth
                      size="large"
                      type="button"
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Button
                      color="primary"
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>

        </Container>
      </Box>
    </>
  )
}

export default AddEditVideo
